import {
  EditUserBtn,
  SystemDetailsContentWrapper,
  SystemDetailsCoverageBlock,
  SystemDetailsCoverageBtnsWrapper,
  SystemDetailsCoverageTitleRow,
  SystemDetailsPageWrapper,
  SystemDetailsUsersBlock,
  SystemDetailSystemInfoWrapper,
} from '@pages/SystemDetailsPage.styles';
import PageTitleBlock from '@organisms/PageTitleBlock';
import Button from '@atoms/Button';
import {CloudMonitoring, Edit, Export} from '@carbon/icons-react';
import {getSystemNotificationsSettings, getSystems, getSystemSnapshot, systems} from '@store/slices/systems';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router';
import PageSubtitleBlock from '@organisms/PageSubtitleBlock';
import PageSubtitleTextBlock from '@molecules/PageSubtitleTextBlock';
import {ERoutes} from '@constants/routes';
import UsersBlock from '@molecules/UsersBlock';
import {theme} from '@styles/theme';
import CoverageBlock from '@molecules/CoverageBlock';
import Text from '@atoms/Text';
import EmptyBlock from '@molecules/EmptyBlock';
import {useEffect, useMemo, useState} from 'react';
import EditAccessRightsModal from '@organisms/modals/EditAccessRightsModal';
import AddEditSubsystemModal from '@organisms/modals/AddEditSubsystemModal';
import SubsystemDetailsBlock from '@organisms/SubsystemDetailsBlock';
import CircleButton from '@atoms/CircleButton';
import EditNotificationsSettingsModal from '@organisms/modals/EditNotificationsSettingsModal';
import ReportExportModal from '@organisms/modals/ReportExportModal';
import {SystemHistory} from '@organisms/SystemHistory';
import {AppDispatch, RootState} from '@store';
import {ISystem} from '@types';
import {IDLE, PENDING} from '@constants/loadingState';
import OptionButton from '@atoms/OptionButton';
import {SystemPlanDropdown} from '@molecules/SystemPlanDropdown';
import AddSystemPlanModal from '@organisms/modals/AddSystemPlanModal';
import {getUsers} from '@store/slices/users';
import {getTests} from '@store/slices/tests';
import {getPlans} from '@store/slices/coveragePlans';
import DeleteSystemModal from '@organisms/modals/DeleteSystemModal';
import AddEditSystemModal from '@organisms/modals/AddEditSystemModal';
import {formatLastUpdate} from '@utils/common';
import {EUserRoles, EUserScopedRoles} from '@constants/usersRoles';

export const SystemDetailsPage = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    loading,
    addLoading,
    editLoading,
    deleteLoading,
    deleteSystemLoading,
    editSystemLoading,
  } = useSelector((state: RootState) => state.systems);
  const [showEditSystem, setShowEditSystem] = useState(false);
  const [systemIsEdited, setSystemIsEdited] = useState(false);
  const [showDeleteSystem, setShowDeleteSystem] = useState(false);
  const [systemIsDeleting, setSystemIsDeleting] = useState(false);
  const [showAddSubsystem, setShowAddSubsystem] = useState(false);
  const [showEditAccessRight, setShowEditAccessRight] = useState(false);
  const [showEditNotifications, setShowEditNotifications] = useState(false);
  const [showAddSystemPlan, setShowAddSystemPlan] = useState(false);
  const [showReportExport, setShowReportExport] = useState(false);
  const systemsArr: ISystem[] = useSelector(systems);
  const currentSystem = systemsArr.find(system => String(system.id) === id);
  const systemOwner =
    currentSystem?.snapshot?.users.filter(user => user.scoped_role_id === 4) || [];
  const administrators =
    currentSystem?.snapshot?.users.filter(user => user.scoped_role_id === 3) || [];
  const editors =
    currentSystem?.snapshot?.users.filter(user => user.scoped_role_id === 1) || [];
  const viewers =
    currentSystem?.snapshot?.users.filter(user => user.scoped_role_id === 2) || [];
  const currentUserID = useSelector((state: RootState) => state.profile.profile?.id);
  const systemRole = useSelector((state: RootState) => state.profile.profile?.role_id);
  const scopedRole = useMemo(() => {
    return currentSystem?.snapshot?.users.find(user => user.id === currentUserID)
      ?.scoped_role_id;
  }, [currentSystem?.snapshot?.users]);

  useEffect(() => {
    dispatch(getSystems({}));
    dispatch(getPlans(Number(id)));
    dispatch(getUsers({}));
    dispatch(getTests({}));
    dispatch(getSystemNotificationsSettings({system_id: Number(id)}));
  }, []);

  useEffect(() => {
    if (
      loading === IDLE &&
      addLoading === IDLE &&
      editLoading === IDLE &&
      deleteLoading === IDLE
    ) {
      dispatch(getSystemSnapshot({system_id: Number(id)}));
    }
  }, [loading, addLoading, editLoading, deleteLoading]);

  useEffect(() => {
    if (editSystemLoading === PENDING) {
      setSystemIsEdited(true);
    } else if (editSystemLoading === IDLE && systemIsEdited) {
      setSystemIsEdited(false);
      dispatch(getSystems({}))
        .unwrap()
        .then(() => {
          dispatch(getSystemSnapshot({system_id: Number(id)}));
        });
    }
  }, [editSystemLoading]);

  useEffect(() => {
    if (deleteSystemLoading === PENDING) {
      setSystemIsDeleting(true);
    } else if (deleteSystemLoading === IDLE && systemIsDeleting) {
      setSystemIsDeleting(false);
      navigate(ERoutes.SYSTEMS);
    }
  }, [deleteSystemLoading]);

  console.log('scopedRole', scopedRole);

  return (
    <SystemDetailsPageWrapper>
      <PageTitleBlock
        title={currentSystem?.name}
        buttons={[
          <Button
            key={'add-subsystem-title'}
            title="Добавить подсистему"
            onClick={() => {
              setShowAddSubsystem(true);
            }}
            disabled={
              systemRole === EUserRoles.BASE_USER &&
              (scopedRole === EUserScopedRoles.VIEWER || !scopedRole)
            }
          />,
          <Button
            key={'generate-report'}
            title="Сформировать отчёт"
            variant={'filled'}
            iconElement={<Export />}
            onClick={() => {
              setShowReportExport(true);
            }}
          />,
          <CircleButton
            key={'system-menu'}
            menu={
              systemRole === EUserRoles.BASE_USER &&
              (!scopedRole || scopedRole === EUserScopedRoles.VIEWER)
                ? []
                : [
                    {
                      title: 'Редактировать',
                      onClick: () => {
                        setShowEditSystem(true);
                      },
                    },
                    {
                      title: 'Добавить подсистему',
                      onClick: () => {
                        setShowAddSubsystem(true);
                      },
                    },
                    {
                      title: 'Настройка уведомлений',
                      onClick: () => {
                        setShowEditNotifications(true);
                      },
                    },
                    {
                      title: 'Удалить',
                      onClick: () => {
                        setShowDeleteSystem(true);
                      },
                      color: theme.colors.signalRed00,
                    },
                  ]
            }
          />,
        ]}
        withBackBtn
        backBtnText="Вернуться к списку систем"
        backBtnLink={ERoutes.SYSTEMS}
        systems={systemsArr}
      />
      <PageSubtitleBlock
        elementsToLeft={[
          <PageSubtitleTextBlock
            title="Всего подсистем:"
            text={`${currentSystem?.subsystems_count}`}
            key="all-systems-count"
          />,
          <PageSubtitleTextBlock
            title="Всего модулей:"
            text={`${currentSystem?.modules_count}`}
            key="all-modules-count"
          />,
          <PageSubtitleTextBlock
            title="Последнее изменение"
            text={`${formatLastUpdate(currentSystem?.last_update || '')}`}
            key="last-change"
          />,
        ]}
        elementsToRight={[]}
      />
      <SystemDetailsContentWrapper>
        <SystemDetailSystemInfoWrapper>
          <SystemDetailsUsersBlock>
            <UsersBlock
              users={administrators}
              systemOwner={systemOwner[0] || undefined}
              title="Администраторы"
            />
            {!!editors.length && <UsersBlock users={editors} title="Редакторы" />}
            {!!viewers.length && <UsersBlock users={viewers} title="Наблюдатели" />}
            {(systemRole !== EUserRoles.BASE_USER ||
              (systemRole === EUserRoles.BASE_USER &&
                scopedRole &&
                scopedRole !== EUserScopedRoles.VIEWER)) && (
              <EditUserBtn onClick={() => setShowEditAccessRight(true)}>
                <Edit width={14} height={13} color={theme.colors.basic00} />
              </EditUserBtn>
            )}
          </SystemDetailsUsersBlock>
          <SystemDetailsCoverageBlock>
            <SystemDetailsCoverageTitleRow>
              <Text text="Обзор общего покрытия системы" type="captionB" />
              <SystemDetailsCoverageBtnsWrapper>
                <SystemHistory />
                <OptionButton
                  value={'1'}
                  options={[]}
                  dropdownContent={
                    <SystemPlanDropdown
                      onAddSystemPlan={() => setShowAddSystemPlan(true)}
                      role={systemRole}
                      scopedRole={scopedRole}
                    />
                  }
                  buttonTitle="Планирование"
                  dropdownAlign="right"
                  iconVariant={'plan'}
                />
              </SystemDetailsCoverageBtnsWrapper>
            </SystemDetailsCoverageTitleRow>
            <CoverageBlock
              autoCoverage={
                currentSystem?.snapshot?.general_auto_coverage ??
                currentSystem?.auto_coverage
              }
              manualCoverage={
                currentSystem?.snapshot?.general_coverage ?? currentSystem?.test_coverage
              }
              tests={currentSystem?.snapshot?.general_coverage_by_tests}
            />
          </SystemDetailsCoverageBlock>
          {!currentSystem?.snapshot?.subsystems?.length && (
            <EmptyBlock
              icon={<CloudMonitoring size={32} color={theme.colors.accent00} />}
              text="Здесь пусто. Добавьте подсистемы и редакторов для администрирования состояния тестового покрытия."
            />
          )}
        </SystemDetailSystemInfoWrapper>
        {currentSystem?.snapshot?.subsystems?.map(subsystem => {
          return (
            <SubsystemDetailsBlock
              key={`subsystem-details-block-${subsystem.id}`}
              subsystem={subsystem}
            />
          );
        })}
      </SystemDetailsContentWrapper>
      <AddEditSystemModal
        isOpened={showEditSystem}
        onCloseRequest={() => setShowEditSystem(false)}
        system={currentSystem}
      />
      <DeleteSystemModal
        isOpened={showDeleteSystem}
        system={currentSystem}
        onCloseRequest={() => setShowDeleteSystem(false)}
      />
      <EditAccessRightsModal
        isOpened={showEditAccessRight}
        onCloseRequest={() => setShowEditAccessRight(false)}
        systemID={Number(id)}
        defaultUsers={
          currentSystem?.snapshot?.users.filter(
            user => user.scoped_role_id !== EUserScopedRoles.SYSTEM_OWNER,
          ) || []
        }
        systemOwnerId={
          currentSystem?.snapshot?.users.find(
            user => user.scoped_role_id === EUserScopedRoles.SYSTEM_OWNER,
          )?.id || 0
        }
        currentScopedRole={scopedRole}
        currentRole={systemRole}
      />
      <AddEditSubsystemModal
        isOpened={showAddSubsystem}
        onCloseRequest={() => setShowAddSubsystem(false)}
        systemID={Number(id)}
        administrators={currentSystem?.snapshot?.users || []}
        currentScopedRole={scopedRole}
        currentRole={systemRole}
      />
      <EditNotificationsSettingsModal
        isOpened={showEditNotifications}
        onCloseRequest={() => setShowEditNotifications(false)}
        system={currentSystem}
      />
      <AddSystemPlanModal
        isOpened={showAddSystemPlan}
        onCloseRequest={() => setShowAddSystemPlan(false)}
      />
      <ReportExportModal
        isOpened={showReportExport}
        onCloseRequest={() => setShowReportExport(false)}
        systemName={currentSystem?.name}
        systemID={currentSystem?.id}
        subsystems={currentSystem?.snapshot?.subsystems}
        afterSnapshotID={currentSystem?.snapshot?.snapshot_id}
      />
    </SystemDetailsPageWrapper>
  );
};

export default SystemDetailsPage;
